/**
 * Focus returns to the invoking row when an inspector closes (docs/ux/review/interaction-visual.md
 * #11): lists mark rows with `data-row-key` (and Now's decision rows with `data-decision-key`),
 * inspectors are keyed by the same value in the URL. When the key clears, the row's main button
 * gets focus back — unless the user already put focus somewhere on purpose.
 */
import { useEffect, useRef } from 'react'

const esc = (s: string) =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/["\\]/g, '\\$&')

export function rowFor(key: string, root: ParentNode = document): HTMLElement | null {
  const row = root.querySelector<HTMLElement>(
    `[data-row-key="${esc(key)}"], [data-decision-key="${esc(key)}"]`,
  )
  return row?.querySelector<HTMLElement>('[data-row-main]') ?? row
}

/** Focus the row for `key` when focus was dropped (on <body>, or inside a removed inspector). */
export function returnFocusTo(key: string): boolean {
  const at = document.activeElement
  if (at && at !== document.body && at.isConnected) return false
  const el = rowFor(key)
  if (!el) return false
  el.focus()
  return document.activeElement === el
}

export function useReturnFocus(openKey: string | undefined) {
  const last = useRef<string | undefined>(openKey)
  useEffect(() => {
    const prev = last.current
    last.current = openKey
    if (openKey || !prev) return
    // After the inspector unmounts and the list re-renders.
    const id = requestAnimationFrame(() => returnFocusTo(prev))
    return () => cancelAnimationFrame(id)
  }, [openKey])
}
